/**
 * Keyboard input. Uses `event.code` (physical key position), so the WASD codes
 * automatically match ZQSD on French AZERTY layouts; we also accept the literal
 * KeyZ/KeyQ codes for ZQSD typed on a QWERTY layout. Arrows always work.
 */

import type { CarInput } from '@microligue/shared';

const UP_CODES = new Set(['ArrowUp', 'KeyW', 'KeyZ']);
const DOWN_CODES = new Set(['ArrowDown', 'KeyS']);
const LEFT_CODES = new Set(['ArrowLeft', 'KeyA', 'KeyQ']);
const RIGHT_CODES = new Set(['ArrowRight', 'KeyD']);

export class KeyboardInput {
  private readonly pressed = new Set<string>();
  /** Edge-triggered action keys consumed once per frame. */
  private readonly actionQueue: string[] = [];

  constructor(target: Window) {
    target.addEventListener('keydown', (e: KeyboardEvent) => {
      // Typing in the tuning panel (sliders, JSON textarea) or using browser
      // shortcuts (Ctrl/Cmd/Alt+…) must never drive the car nor be swallowed.
      if (isEditingContext(e) || e.ctrlKey || e.metaKey || e.altKey) return;
      // preventDefault also on auto-repeated events, otherwise held arrows
      // scroll the page.
      if (this.isGameKey(e.code)) e.preventDefault();
      if (e.repeat) return;
      this.pressed.add(e.code);
      if (e.code === 'KeyR' || e.code === 'KeyT' || e.code === 'KeyG') {
        this.actionQueue.push(e.code);
      }
    });
    target.addEventListener('keyup', (e: KeyboardEvent) => {
      this.pressed.delete(e.code);
    });
    // Avoid stuck keys when the tab loses focus mid-press.
    target.addEventListener('blur', () => this.pressed.clear());
  }

  private isGameKey(code: string): boolean {
    return (
      UP_CODES.has(code) ||
      DOWN_CODES.has(code) ||
      LEFT_CODES.has(code) ||
      RIGHT_CODES.has(code) ||
      code === 'Space'
    );
  }

  private anyPressed(codes: Set<string>): boolean {
    for (const c of codes) if (this.pressed.has(c)) return true;
    return false;
  }

  /** Current driving input — sampled once per simulation tick. */
  sample(): CarInput {
    const left = this.anyPressed(LEFT_CODES) ? 1 : 0;
    const right = this.anyPressed(RIGHT_CODES) ? 1 : 0;
    return {
      throttle: this.anyPressed(UP_CODES) ? 1 : 0,
      brake: this.anyPressed(DOWN_CODES) ? 1 : 0,
      steer: right - left,
    };
  }

  /** Returns queued one-shot actions ('KeyR', 'KeyT', 'KeyG') and clears the queue. */
  drainActions(): string[] {
    return this.actionQueue.splice(0, this.actionQueue.length);
  }
}

function isEditingContext(e: KeyboardEvent): boolean {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return false;
  return (
    t instanceof HTMLInputElement ||
    t instanceof HTMLTextAreaElement ||
    t instanceof HTMLSelectElement ||
    t.isContentEditable
  );
}
